<template>
    <div class="menu-bar">
        <slot name="left"></slot>

        <!-- Gap for desktop -->
        <div class="flex--grow flex--shrink desktop-only"></div>

        <!-- Clock (desktop only) -->
        <slot name="clock">
            <MenuBarClock class="desktop-only" />
        </slot>

        <!-- Online button (desktop only) -->
        <!-- Hidden if not logged in because it will show offline if you're not logged in. -->
        <NavOnlineBtn v-if="showConnectivity && loggedIn"
                      class="desktop-only btn-dark-blue" />

        <NavWebsocketConnectionBtn v-if="showWebsocketConnectivity" />

        <!-- ProPoint update button -->
        <ProPointUpdate v-if="showUpdate"
                        class="desktop-only" />

        <slot name="right"></slot>
    </div>
</template>

<script>
import MenuBarClock from '@/vue/components/Menus/MenuBarClock';
import NavOnlineBtn from '@/vue/components/Menus/NavOnlineBtn';
import NavWebsocketConnectionBtn from '@/vue/components/Menus/NavWebsocketConnectionBtn';
import ProPointUpdate from '@/vue/components/Menus/ProPointUpdate';
import { mapGetters, mapState } from 'vuex';

export default {
    name: 'AbstractMenuBar',

    components: {
        NavWebsocketConnectionBtn,
        MenuBarClock,
        NavOnlineBtn,
        ProPointUpdate,
    },

    props: {
        showBranchSelector: {
            type: Boolean,
            default: false,
        },

        showConnectivity: {
            type: Boolean,
            default: false,
        },

        showWebsocketConnectivity: {
            type: Boolean,
            default: false,
        },

        showUpdate: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapGetters([
            'loggedIn',
            'updateAvailable',
        ]),
        ...mapState([
            'currentBranch',
        ]),
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../less/global.less";

.menu-bar {
    position: absolute;
    display: flex;
    z-index: @zindex-navbar + 1; // 1 higher than Bootstrap's navbar.
    align-items: stretch;
    justify-content: flex-start;
    left: 0;
    right: 0;
    height: @menu-bar-height;

    .btn {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 55px;
        box-shadow: none !important;
        // Buttons get squashed if anything else in the bar gets too wide, so let the
        // things that can cope with it (the branch name, the clock) give up their space instead.
        flex-shrink: 0;

        .text {
            display: none;
        }
    }

    .branch-selector,
    .order-branch-selector {
        height: 100%;
    }
}

.dummy-branch-selector-btn {
    color: @white;
    font-size: 15px;
    padding: 0 5px;
}

@media (max-width: @mobile-max) {
    .menu-bar {
        position: fixed;
        bottom: 0;
        border-top: 1px solid @dt-light-grey;

        .btn {
            flex-grow: 1;
            flex-shrink: 1;
            background: @white;
            border-radius: 0;
            border: 0;
            color: @dt-navy;
            min-width: 0;

            &.active {
                background: @dt-dark-blue;
                color: @white;
            }

            i {
                font-size: @menu-bar-mobile-icon-size;

                &.update-available {
                    color: @brand-warning;
                }
            }
        }
    }
}

@media (min-width: @screen-md-min) {
    .menu-bar {
        .btn {
            min-width: 50px;

            i:not(:last-child) {
                margin-right: 5px;
            }

            .text {
                display: block;
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .menu-bar {
        top: 0;
        background: @dark-blue;
        padding: 7px;
        grid-gap: 7px; // 7px space between each menu item.
        align-items: center;

        .btn {
            &.active {
                background: @white;
                color: @dt-dark-blue;
            }

            padding: 0 9px 1px;
        }
    }
}
</style>
